<template>
  <div class="cliente-selector-wrapper">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      transition="scale-transition"
      :offset-y="!isMobile"
      :max-width="isMobile ? '95vw' : '480'"
      :min-width="isMobile ? '95vw' : '420'"
      :content-class="isMobile ? 'mobile-menu' : ''"
      :nudge-bottom="isMobile ? 0 : 10"
      :top="isMobile"
      :bottom="!isMobile"
      :attach="isMobile ? 'body' : false"
    >
      <!-- ── Activator ─────────────────────────────────────────────────── -->
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="displayCliente"
          :placeholder="placeholder"
          :label="label"
          prepend-inner-icon="mdi-account-search-outline"
          readonly
          dense
          outlined
          hide-details
          :disabled="disabled"
          :loading="loading"
          :clearable="clearable && !!value"
          class="cliente-field"
          v-bind="attrs"
          v-on="on"
          @click:clear="limpar"
        >
          <template v-slot:append v-if="value && showCheckIcon">
            <v-icon color="success">mdi-check-circle</v-icon>
          </template>
        </v-text-field>
      </template>

      <!-- ── Menu ─────────────────────────────────────────────────────── -->
      <v-card class="cliente-menu-card" :class="{ 'mobile-card': isMobile }">

        <!-- Header mobile -->
        <v-card-title v-if="isMobile" class="mobile-header">
          <div class="mobile-header-content">
            <v-icon color="white" small>mdi-account-search-outline</v-icon>
            <span class="mobile-header-title">Buscar Cliente</span>
          </div>
          <v-btn icon small color="white" @click="menu = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Busca -->
        <v-card-text class="py-2 px-3 busca-section">
          <v-text-field
            v-model="busca"
            placeholder="Nome ou código do cliente..."
            outlined dense hide-details clearable
            prepend-inner-icon="mdi-magnify"
            autofocus
            class="busca-field"
            @input="onBuscaInput"
          />
        </v-card-text>

        <v-divider />

        <!-- Lista -->
        <div class="cliente-list-wrapper">

          <!-- Aguardando digitação (debounce pendente) -->
          <div v-if="aguardando" class="cliente-list-hint">
            <v-icon color="grey lighten-2" class="mb-1">mdi-keyboard-outline</v-icon>
            <span class="text-caption grey--text">Aguardando você terminar de digitar...</span>
          </div>

          <!-- Loading (requisição em andamento) -->
          <div v-else-if="carregando" class="cliente-list-loading">
            <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
            <span class="text-caption grey--text">Buscando clientes...</span>
          </div>

          <!-- Hint inicial -->
          <div v-else-if="!busca || busca.length < 2" class="cliente-list-hint">
            <v-icon color="grey lighten-2" class="mb-1">mdi-magnify</v-icon>
            <span class="text-caption grey--text">Digite ao menos 2 caracteres para buscar</span>
          </div>

          <!-- Vazio -->
          <div v-else-if="!clientes.length" class="cliente-list-hint">
            <v-icon color="grey lighten-2" class="mb-1">mdi-account-off-outline</v-icon>
            <span class="text-caption grey--text">Nenhum cliente encontrado</span>
          </div>

          <!-- Resultados -->
          <v-virtual-scroll
            v-else
            :items="clientes"
            :height="Math.min(clientes.length * 58, 290)"
            item-height="58"
          >
            <template v-slot:default="{ item }">
              <div class="cliente-item-wrapper">
                <v-list-item
                  dense
                  class="cliente-item"
                  :class="{ 'cliente-selecionado': isSelecionado(item) }"
                  @click="selecionar(item)"
                >
                  <v-list-item-avatar size="32" color="#e0f4f8" class="mr-2 my-0">
                    <v-icon size="18" color="#023449">
                      {{ item.tipofisicajuridica === 'J' ? 'mdi-domain' : 'mdi-account-outline' }}
                    </v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content class="py-1">
                    <v-list-item-title class="cliente-nome">{{ item.nome }}</v-list-item-title>
                    <v-list-item-subtitle class="cliente-info">
                      Cód: {{ item.idcliente }}
                      <template v-if="item.cpfcnpj"> · {{ item.cpfcnpj }}</template>
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action v-if="isSelecionado(item)" class="my-0">
                    <v-icon small color="success">mdi-check-circle</v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-divider class="item-divider" />
              </div>
            </template>
          </v-virtual-scroll>
        </div>

        <!-- Footer -->
        <template v-if="value">
          <v-divider />
          <v-card-actions class="py-2 px-3 menu-footer">
            <div class="d-flex align-center">
              <v-chip x-small outlined color="#023449" class="count-chip">
                <v-icon x-small left>mdi-check-circle</v-icon>
                {{ value.nome || value.idcliente }}
              </v-chip>
            </div>
            <v-spacer />
            <v-btn x-small text color="error" @click="limpar" class="action-btn">
              <v-icon x-small left>mdi-close-circle</v-icon>
              Limpar
            </v-btn>
          </v-card-actions>
        </template>

      </v-card>
    </v-menu>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'

export default {
  name: 'SeletorCliente',

  props: {
    value:         { type: Object,  default: null  },
    label:         { type: String,  default: 'Cliente' },
    placeholder:   { type: String,  default: 'Selecione o cliente' },
    disabled:      { type: Boolean, default: false },
    loading:       { type: Boolean, default: false },
    clearable:     { type: Boolean, default: true  },
    showCheckIcon: { type: Boolean, default: true  },
    somente_clientes: { type: Boolean, default: false },
  },

  data() {
    return {
      menu:         false,
      busca:        '',
      clientes:     [],
      carregando:   false,
      aguardando:   false,  // debounce pendente (usuário ainda digitando)
      debounce:     null,
      reqId:        0,
      windowWidth:  window.innerWidth,
    }
  },

  computed: {
    ...mapState(['backendUrl']),

    isMobile() {
      return this.windowWidth < 960
    },

    displayCliente() {
      if (!this.value) return ''
      return this.value.nome || `Cliente #${this.value.idcliente}`
    },
  },

  watch: {
    menu(aberto) {
      if (aberto) {
        this.busca      = ''
        this.clientes   = []
        this.reqId      = 0
        this.aguardando = false
      }
      if (aberto && this.isMobile) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.debounce) clearTimeout(this.debounce)
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },

    onBuscaInput(val) {
      if (this.debounce) clearTimeout(this.debounce)

      if (!val || val.length < 2) {
        this.clientes   = []
        this.carregando = false
        this.aguardando = false
        return
      }

      // sinaliza que o usuário está digitando e a busca está aguardando
      this.aguardando = true
      this.carregando = false

      this.debounce = setTimeout(() => {
        this.aguardando = false
        this.carregando = true
        this.buscarClientes(val)
      }, 1000)
    },

    buscarClientes(val) {
      // incrementa o ID e guarda o desta chamada
      const id = ++this.reqId

      axios
        .post(`${this.backendUrl}cliente/listar`, {
          limit:            30,
          offset:           0,
          busca:            val || null,
          somente_clientes: this.somente_clientes,
        })
        .then(res => {
          // só atualiza se esta ainda for a requisição mais recente
          if (id === this.reqId) {
            this.clientes = res.data?.lista || []
          }
        })
        .catch(() => {
          if (id === this.reqId) this.clientes = []
        })
        .finally(() => {
          if (id === this.reqId) this.carregando = false
        })
    },

    isSelecionado(item) {
      return this.value && this.value.idcliente === item.idcliente
    },

    selecionar(item) {
      this.$emit('input',  item)
      this.$emit('change', item)
      this.menu = false
    },

    limpar() {
      this.$emit('input',  null)
      this.$emit('change', null)
      this.$emit('clear')
    },
  },
}
</script>

<style scoped>
.cliente-selector-wrapper { width: 100%; }

/* ── Campo ───────────────────────────────────────────────────────────── */
.cliente-field >>> .v-input__control { min-height: 44px !important; }
.cliente-field >>> fieldset {
  border-color: #cbd5e1;
  border-width: 2px;
  transition: all 0.2s ease;
}
.cliente-field >>> .v-input__slot {
  background: #f8fafc;
  transition: all 0.2s ease;
  border-radius: 10px;
}
.cliente-field >>> .v-input__slot:hover fieldset { border-color: #023449; }
.cliente-field >>> .v-input--is-focused fieldset {
  border-color: #023449 !important;
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(2, 52, 73, 0.1);
}
.cliente-field >>> .v-input--is-focused .v-input__slot { background: #ffffff !important; }

/* ── Menu card ───────────────────────────────────────────────────────── */
.cliente-menu-card {
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(2, 52, 73, 0.15) !important;
  overflow: hidden;
}
.mobile-card {
  max-width: 95vw !important;
  width: 95vw !important;
  max-height: 85vh !important;
  display: flex;
  flex-direction: column;
}

/* ── Header mobile ───────────────────────────────────────────────────── */
.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: linear-gradient(135deg, #023449 0%, #035a7a 100%);
  color: white;
  flex-shrink: 0;
}
.mobile-header-content { display: flex; align-items: center; gap: 8px; }
.mobile-header-title   { font-size: 0.875rem; font-weight: 700; letter-spacing: 0.3px; }

/* ── Busca ───────────────────────────────────────────────────────────── */
.busca-section { flex-shrink: 0; }
.busca-field >>> .v-input__control { min-height: 36px !important; }
.busca-field >>> .v-input__slot    { background: #f8fafc; border-radius: 8px; min-height: 36px !important; }
.busca-field >>> fieldset          { border-color: #cbd5e1; border-width: 2px; }
.busca-field >>> .v-input__slot:hover fieldset     { border-color: #023449; }
.busca-field >>> .v-input--is-focused fieldset     { border-color: #023449 !important; }
.busca-field >>> .v-text-field__details            { display: none; }

/* ── Lista ───────────────────────────────────────────────────────────── */
.cliente-list-wrapper {
  background: #fff;
  min-height: 80px;
  overflow-y: auto;
}

.cliente-list-loading,
.cliente-list-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  gap: 4px;
}

.cliente-item-wrapper { background: #fff; }

.cliente-item {
  min-height: 56px !important;
  max-height: 58px !important;
  padding: 4px 12px !important;
  cursor: pointer;
  transition: background-color 0.15s ease;
  background: #fff !important;
}
.cliente-item:hover    { background-color: rgba(2, 52, 73, 0.04) !important; }
.cliente-selecionado   { background-color: rgba(2, 52, 73, 0.07) !important; }

.item-divider { margin: 0 !important; border-color: #e2e8f0 !important; }

.cliente-nome {
  font-size: 0.813rem;
  font-weight: 600;
  color: #023449;
  line-height: 1.2;
}
.cliente-info {
  font-size: 0.688rem;
  color: #64748b;
  margin-top: 2px;
  line-height: 1.2;
}

/* ── Footer ──────────────────────────────────────────────────────────── */
.menu-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  min-height: 44px !important;
  display: flex;
  align-items: center;
}
.count-chip  { font-weight: 600; border-width: 1.5px !important; height: 20px !important; font-size: 0.688rem; max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
.action-btn  { text-transform: none !important; font-weight: 600 !important; font-size: 0.688rem !important; height: 28px !important; padding: 0 10px !important; border-radius: 6px; }
.action-btn:hover:not(.v-btn--disabled) { background-color: rgba(220, 38, 38, 0.08) !important; color: #dc2626 !important; }

/* ── Mobile menu ─────────────────────────────────────────────────────── */
.mobile-menu {
  position: fixed !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 95vw !important;
  max-height: 85vh !important;
}

/* ── Scrollbar ───────────────────────────────────────────────────────── */
.cliente-list-wrapper::-webkit-scrollbar       { width: 3px; }
.cliente-list-wrapper::-webkit-scrollbar-track { background: #f1f5f9; }
.cliente-list-wrapper::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
</style>
